import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  ShieldCheck, ShieldAlert, ShieldX, ShieldQuestion, Loader2, Save, User, Building2,
  ChevronLeft, CheckCircle2, XCircle, Clock, ArrowUpCircle,
} from "lucide-react";
import PrivateLayout from "../components/PrivateLayout";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../services/api";

const INDIVIDUAL_ROLES = new Set(["talent", "freelance", "coach", "investisseur"]);
const BUSINESS_ROLES  = new Set(["employeur", "universite", "entrepreneur"]);

/**
 * MyKyc — /mon-kyc · Vérification d'identité KYC (particulier) ou KYB (entreprise).
 * Additif — ne modifie aucune route/collection existante.
 */
export default function MyKyc() {
  const { user } = useAuth();
  const suggestedType = BUSINESS_ROLES.has(user?.role) ? "kyb" : "kyc";
  const [type, setType] = useState(suggestedType);
  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [personal, setPersonal] = useState({
    full_name: "", date_of_birth: "", nationality: "", country_residence: "",
    id_type: "cni", id_number: "", phone: "", address: "",
  });
  const [business, setBusiness] = useState({
    legal_name: "", trade_name: "", registration_number: "", incorporation_country: "",
    incorporation_date: "", sector: "", legal_rep_name: "", legal_rep_role: "", tax_id: "", address: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/v1/kyc/me");
      setVerification(data.verification);
      if (data.verification?.personal) setPersonal((p) => ({ ...p, ...data.verification.personal }));
      if (data.verification?.business) setBusiness((b) => ({ ...b, ...data.verification.business }));
      if (data.verification?.type) setType(data.verification.type);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user?.id) load(); }, [user?.id, load]);

  const submit = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const body = { type };
      if (type === "kyc") body.personal = personal;
      else body.business = business;
      const { data } = await api.post("/v1/kyc/submit", body);
      setVerification(data.verification);
      toast.success("Dossier envoyé · en attente de validation par AFRICTAL");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const status = verification?.status || "not_started";
  const banner = STATUS_BANNERS[status];

  return (
    <PrivateLayout
      title="🛡️ Vérification d'identité (KYC / KYB)"
      breadcrumbs={[{ to: "/dashboard", label: "Tableau de bord" }, { label: "KYC / KYB" }]}
      actions={<Link to="/mon-wallet" className="afr-btn afr-btn--outline afr-btn--sm" data-testid="kyc-back-wallet"><ChevronLeft size={14} /> Wallet</Link>}
      data-testid="mykyc-page"
    >
      {loading ? (
        <div data-testid="kyc-loading">Chargement…</div>
      ) : (
        <>
          {/* Statut banner */}
          <article className="afr-card" data-testid={`kyc-status-banner-${status}`} style={{
            padding: 20, marginBottom: 20, borderLeft: `4px solid ${banner.color}`,
            background: banner.bg,
          }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
              <banner.Icon size={30} style={{ color: banner.color, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: "1.05rem", color: banner.color }}>{banner.title}</div>
                <div className="afr-muted" style={{ marginTop: 4, fontSize: "0.88rem" }}>{banner.body}</div>
                {verification?.motif && (
                  <div style={{ marginTop: 10, padding: 10, background: "rgba(220,38,38,0.08)", borderRadius: 8 }}>
                    <strong>Motif :</strong> {verification.motif}
                  </div>
                )}
                {verification?.reviewed_at && (
                  <div className="afr-muted" style={{ fontSize: "0.78rem", marginTop: 6 }}>
                    Décision : {new Date(verification.reviewed_at).toLocaleString("fr-FR")}
                  </div>
                )}
              </div>
            </div>
          </article>

          {/* Choix type */}
          <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }} data-testid="kyc-type-choice">
            <button type="button" onClick={() => setType("kyc")}
              className={`afr-btn ${type === "kyc" ? "afr-btn--primary" : "afr-btn--outline"}`}
              data-testid="kyc-type-individual">
              <User size={14} /> Particulier (KYC)
            </button>
            <button type="button" onClick={() => setType("kyb")}
              className={`afr-btn ${type === "kyb" ? "afr-btn--primary" : "afr-btn--outline"}`}
              data-testid="kyc-type-business">
              <Building2 size={14} /> Entreprise (KYB)
            </button>
          </div>

          <form onSubmit={submit}>
            {type === "kyc" ? (
              <PersonalForm value={personal} onChange={setPersonal} />
            ) : (
              <BusinessForm value={business} onChange={setBusiness} />
            )}

            <div style={{ display: "flex", gap: 10, marginTop: 20, alignItems: "center" }}>
              <button type="submit" className="afr-btn afr-btn--primary" disabled={saving} data-testid="kyc-submit-btn">
                {saving ? <Loader2 size={14} className="afr-spin" /> : <Save size={14} />}
                {status === "approved" ? "Mettre à jour et re-soumettre" : "Envoyer pour validation"}
              </button>
              <span className="afr-muted" style={{ fontSize: "0.78rem" }}>
                Les données sont chiffrées et utilisées uniquement pour la conformité.
              </span>
            </div>
          </form>
        </>
      )}
    </PrivateLayout>
  );
}

const STATUS_BANNERS = {
  not_started: { Icon: ShieldQuestion, color: "#0F2A42", bg: "rgba(15,42,66,0.06)",
    title: "Vérification requise",
    body: "Complétez le formulaire ci-dessous pour activer les retraits internationaux et l'Escrow AFRICTAL." },
  pending: { Icon: Clock, color: "#B45309", bg: "rgba(180,83,9,0.08)",
    title: "En attente de validation",
    body: "Votre dossier a été envoyé. AFRICTAL examine vos documents (délai indicatif 24–72 h)." },
  approved: { Icon: ShieldCheck, color: "#15803D", bg: "rgba(21,128,61,0.10)",
    title: "Identité vérifiée ✅",
    body: "Vous pouvez utiliser toutes les fonctionnalités de retrait et d'Escrow AFRICTAL." },
  rejected: { Icon: ShieldX, color: "#B91C1C", bg: "rgba(185,28,28,0.08)",
    title: "Dossier refusé",
    body: "Corrigez les points indiqués ci-dessous puis soumettez à nouveau votre dossier." },
};

function Field({ label, testid, children, hint, required }) {
  return (
    <label style={{ display: "block", marginBottom: 12 }} data-testid={`field-${testid}`}>
      <span style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, marginBottom: 4 }}>
        {label} {required && <span style={{ color: "#B91C1C" }}>*</span>}
      </span>
      {children}
      {hint && <span className="afr-muted" style={{ fontSize: "0.72rem", display: "block", marginTop: 2 }}>{hint}</span>}
    </label>
  );
}

function PersonalForm({ value, onChange }) {
  const set = (k) => (e) => onChange((v) => ({ ...v, [k]: e.target.value }));
  return (
    <article className="afr-card" style={{ padding: 20 }} data-testid="kyc-personal-form">
      <h3 style={{ marginTop: 0 }}><User size={18} style={{ verticalAlign: "middle" }} /> Identité personnelle</h3>
      <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10 }}>
        <Field label="Nom complet" testid="full-name" required>
          <input required value={value.full_name} onChange={set("full_name")} data-testid="kyc-full-name" placeholder="Aminata Diop" />
        </Field>
        <Field label="Date de naissance" testid="dob" required>
          <input required type="date" value={value.date_of_birth} onChange={set("date_of_birth")} data-testid="kyc-dob" />
        </Field>
        <Field label="Nationalité" testid="nationality" required>
          <input required value={value.nationality} onChange={set("nationality")} data-testid="kyc-nationality" placeholder="SN" />
        </Field>
        <Field label="Pays de résidence" testid="country" required>
          <input required value={value.country_residence} onChange={set("country_residence")} data-testid="kyc-country" placeholder="SN" />
        </Field>
        <Field label="Type de pièce" testid="id-type" required>
          <select required value={value.id_type} onChange={set("id_type")} data-testid="kyc-id-type">
            <option value="cni">Carte d'identité nationale</option>
            <option value="passport">Passeport</option>
            <option value="driving_license">Permis de conduire</option>
            <option value="residence_permit">Titre de séjour</option>
          </select>
        </Field>
        <Field label="Numéro pièce" testid="id-number" required>
          <input required value={value.id_number} onChange={set("id_number")} data-testid="kyc-id-number" />
        </Field>
        <Field label="Téléphone" testid="phone">
          <input value={value.phone} onChange={set("phone")} data-testid="kyc-phone" placeholder="+221 77 000 00 00" />
        </Field>
        <Field label="Adresse" testid="address">
          <input value={value.address} onChange={set("address")} data-testid="kyc-address" />
        </Field>
      </div>
      <div className="afr-muted" style={{ fontSize: "0.72rem", marginTop: 10 }}>
        Nous vous demanderons prochainement d'uploader une photo de votre pièce et un selfie (Cloudinary sécurisé).
      </div>
    </article>
  );
}

function BusinessForm({ value, onChange }) {
  const set = (k) => (e) => onChange((v) => ({ ...v, [k]: e.target.value }));
  return (
    <article className="afr-card" style={{ padding: 20 }} data-testid="kyc-business-form">
      <h3 style={{ marginTop: 0 }}><Building2 size={18} style={{ verticalAlign: "middle" }} /> Identité de l'entreprise</h3>
      <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10 }}>
        <Field label="Raison sociale" testid="legal-name" required>
          <input required value={value.legal_name} onChange={set("legal_name")} data-testid="kyb-legal-name" placeholder="AFRICTAL SARL" />
        </Field>
        <Field label="Nom commercial" testid="trade-name">
          <input value={value.trade_name} onChange={set("trade_name")} data-testid="kyb-trade-name" />
        </Field>
        <Field label="N° d'enregistrement (RCCM, Reg. Commerce)" testid="reg-number" required>
          <input required value={value.registration_number} onChange={set("registration_number")} data-testid="kyb-reg-number" />
        </Field>
        <Field label="Pays d'immatriculation" testid="incorporation-country" required>
          <input required value={value.incorporation_country} onChange={set("incorporation_country")} data-testid="kyb-incorporation-country" placeholder="SN" />
        </Field>
        <Field label="Date d'immatriculation" testid="incorporation-date">
          <input type="date" value={value.incorporation_date} onChange={set("incorporation_date")} data-testid="kyb-incorporation-date" />
        </Field>
        <Field label="Secteur d'activité" testid="sector">
          <input value={value.sector} onChange={set("sector")} data-testid="kyb-sector" placeholder="Technologies · Éducation · …" />
        </Field>
        <Field label="Représentant légal" testid="legal-rep-name" required>
          <input required value={value.legal_rep_name} onChange={set("legal_rep_name")} data-testid="kyb-legal-rep-name" />
        </Field>
        <Field label="Fonction du représentant" testid="legal-rep-role" required>
          <input required value={value.legal_rep_role} onChange={set("legal_rep_role")} data-testid="kyb-legal-rep-role" placeholder="CEO · Directrice Générale" />
        </Field>
        <Field label="N° d'identification fiscale" testid="tax-id">
          <input value={value.tax_id} onChange={set("tax_id")} data-testid="kyb-tax-id" />
        </Field>
        <Field label="Adresse du siège" testid="business-address">
          <input value={value.address} onChange={set("address")} data-testid="kyb-address" />
        </Field>
      </div>
      <div className="afr-muted" style={{ fontSize: "0.72rem", marginTop: 10 }}>
        Documents à fournir prochainement : RCCM ou équivalent, statuts, KBIS, justificatif de résidence du représentant.
      </div>
    </article>
  );
}
